import { FilterList, Search } from '@mui/icons-material';
import {
  Badge,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Box } from '@mui/system';
import _ from 'lodash';
import type { ChangeEvent, FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { usePopover } from 'src/hooks/use-popover';
import { getAllPatientsByAction, resetPatientRecordAction } from 'src/redux/actions/patientAction';

interface MultiSelectProps {
  options: { label: string; value: unknown }[];
  setPageNo?: any;
  pageNo: number;
  handleSortMultiselect: any;
  setAppliedValues: any;
  appliedValues: any;
  sortConfig: any;
  onClearAll?: () => void;
}

export const MultiSelectWithSearch: FC<MultiSelectProps> = (props) => {
  const {
    options,
    handleSortMultiselect,
    setAppliedValues,
    sortConfig,
    appliedValues,
    pageNo,
    setPageNo,
    onClearAll,
  } = props;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const popover = usePopover<HTMLButtonElement>();
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const totalPatientRes = useSelector((state: any) => state.getAllPatientsReducer);
  const [selectedValues, setSelectedValues] = useState<any>([]);
  const handleValueChange = (
    event: ChangeEvent<HTMLInputElement>,
    option: { label: string; value: unknown }
  ): void => {
    const { value: checkedValue, checked } = event.target;
    let updatedSelectedValues = [...selectedValues];

    if (checked) {
      if (!updatedSelectedValues.find((item) => item.value === checkedValue)) {
        updatedSelectedValues.push(option);
      }
    } else {
      updatedSelectedValues = updatedSelectedValues.filter((item) => item.value !== checkedValue);
    }

    setSelectedValues(updatedSelectedValues);
  };

  useEffect(() => {
    if (popover.open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [popover.open]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPageNo(0);
  };

  //   Debounced search effect
  useEffect(() => {
    const debounceSearch = _.debounce(() => {
      dispatch(resetPatientRecordAction());
      dispatch(
        getAllPatientsByAction({
          data: {
            searchString: searchTerm?.trim(),
          },
          params: {
            page: 0,
            pageSize: 20,
            active: true,
          },
        })
      );
    }, 1000);

    debounceSearch();

    return () => debounceSearch.cancel();
  }, [searchTerm, pageNo, dispatch]);

  // Defer parent state changes (which trigger the list API) until Apply.
  // Clear should only reset the menu's local UI state and queue the sort
  // reset; nothing should fire until the user clicks Apply.
  const [pendingSortReset, setPendingSortReset] = useState(false);

  const handleClearFilter = () => {
    setIsSelectAll(false);
    setSelectedValues([]);
    setSearchTerm('');
    if (typeof setPageNo === 'function') setPageNo(0);
    setPendingSortReset(true);
  };

  const handleApply = () => {
    setAppliedValues(selectedValues);
    if (pendingSortReset && typeof onClearAll === 'function') {
      onClearAll();
    }
    setPendingSortReset(false);
    popover.handleClose();
  };
  const handleOpenPopover = () => {
    setSearchTerm('');
    setSelectedValues(appliedValues?.length ? appliedValues : []);
    setPendingSortReset(false);
    popover.handleOpen();
  };

  const [isSelectAll, setIsSelectAll] = useState(false);
  const handleSelectAll = () => {
    setIsSelectAll(true);
  };

  useMemo(() => {
    if (isSelectAll) {
      const getAllListItem = totalPatientRes?.totalPatients.map((res: any) => {
        return {
          label: `${res?.lastName}, ${res?.firstName}`,
          value: res?.uuid,
        };
      });
      setSelectedValues(getAllListItem);
    }
  }, [totalPatientRes, isSelectAll]);

  const avoidBubblingUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  // Pin ALL currently selected options to the top — even ones from paginated
  // pages that haven't been re-fetched yet — so reopening the filter shows
  // every prior pick at the top instead of only the page-1 ones.
  const selectedValuesArray: { label: string; value: unknown }[] = Array.isArray(selectedValues)
    ? selectedValues
    : [];
  const selectedValueSet = new Set(selectedValuesArray.map((s) => s?.value));
  const matchesSearch = (option: { label?: string }) => {
    if (!searchTerm) return true;
    const normalizedLabel = option?.label?.replace(/[\s,]/g, '')?.toLowerCase() ?? '';
    const normalizedSearchTerm = searchTerm.replace(/[\s,]/g, '').toLowerCase();
    return normalizedLabel.includes(normalizedSearchTerm);
  };
  const pinnedSelected = selectedValuesArray.filter(matchesSearch);
  const restOptions = (options ?? []).filter((opt: any) => !selectedValueSet.has(opt?.value));
  const orderedOptions = [...pinnedSelected, ...restOptions];

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpenPopover}
        ref={popover.anchorRef}
        sx={{
          padding: '0px',
        }}
      >
        <Badge
          badgeContent={appliedValues?.length}
          color="primary"
        >
          <FilterList sx={{ color: '#6C737F', padding: '0px' }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        PaperProps={{
          sx: {
            width: 260,
            '& .MuiMenuItem-root + .MuiDivider-root': {
              marginTop: '0px',
            },
          },
        }}
      >
        <MenuItem
          sx={{
            background:
              !pendingSortReset &&
              sortConfig.key === 'patientName' &&
              sortConfig.direction === 'asc'
                ? '#d7e4faa3'
                : 'none',
            '&:hover': {
              background:
                !pendingSortReset &&
                sortConfig.key === 'patientName' &&
                sortConfig.direction === 'asc'
                  ? '#d7e4faa3'
                  : 'none',
            },
          }}
          onClick={() => {
            setPendingSortReset(false);
            handleSortMultiselect('PatientName', 'asc');
          }}
        >
          {'Sort A-Z'}
        </MenuItem>
        <MenuItem
          sx={{
            background:
              !pendingSortReset &&
              sortConfig.key === 'patientName' &&
              sortConfig.direction === 'desc'
                ? '#d7e4faa3'
                : 'none',
            '&:hover': {
              background:
                !pendingSortReset &&
                sortConfig.key === 'patientName' &&
                sortConfig.direction === 'desc'
                  ? '#d7e4faa3'
                  : 'none',
            },
          }}
          onClick={() => {
            setPendingSortReset(false);
            handleSortMultiselect('PatientName', 'desc');
          }}
        >
          {'Sort Z-A'}
        </MenuItem>

        {/* <MenuItem onClick={handleSelectAll}>{'Select all'}</MenuItem> */}
        <MenuItem
          onClick={handleClearFilter}
          disabled={!onClearAll && !selectedValues?.length}
        >
          Clear
        </MenuItem>
        <Divider sx={{ marginTop: 0 }} />
        <TextField
          variant="outlined"
          placeholder="Search by Patient"
          fullWidth
          onChange={handleSearchInputChange}
          value={searchTerm}
          sx={{
            p: 1,
            pt: 0,
            '& .MuiInputBase-input': {
              padding: '12px 0px',
            },
          }}
          inputRef={inputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          onKeyDown={avoidBubblingUp}
        />
        <Box>
          <InfiniteScroll
            dataLength={totalPatientRes?.totalPatients?.length}
            next={() => {
              dispatch(
                getAllPatientsByAction({
                  data: {
                    searchString: searchTerm?.trim(),
                  },
                  params: {
                    page: totalPatientRes?.pageNumber,
                    pageSize: 20,
                    active: true,
                  },
                })
              );
            }}
            style={{
              overflow: 'scroll',
              maxHeight: 250,
            }}
            height={400}
            hasMore={
              totalPatientRes?.response?.data?.totalElements !==
              totalPatientRes?.totalPatients?.length
            }
            loader={
              <Box
                display="flex"
                justifyContent="center"
                p={2}
              >
                <CircularProgress />
              </Box>
            }
            endMessage={
              <Box
                textAlign="center"
                p={3}
              >
                {totalPatientRes?.response?.data?.totalElements === 0 ? (
                  <Typography
                    color={'text.secondary'}
                    variant="subtitle2"
                  >
                    No results found
                  </Typography>
                ) : null}
              </Box>
            }
          >
            {orderedOptions?.map((option: any) => (
              <Box
                key={option.value}
                px={2}
                sx={{ py: '2px' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedValues.some((item: any) => item.value === option.value)}
                      onChange={(event) => handleValueChange(event, option)}
                      value={option.value}
                      sx={{
                        padding: '6px',
                      }}
                    />
                  }
                  label={option.label}
                />
              </Box>
            ))}
          </InfiniteScroll>
        </Box>
        <Box
          p={2}
          pb={0.5}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={popover.handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default MultiSelectWithSearch;
